import React from 'react';
import PipelineAPI from '../../../api/pipeline';
import {
  CastMember,
  PipelineRun,
  ReviewCorrection,
  ReviewFinding,
  ReviewTrace,
  ReviewVerdict,
} from '../../../api/structs';
import useReviewProfile from '../../../hooks/useReviewProfile';
import useReviewTraces from '../../../hooks/useReviewTraces';
import ReviewerGraph from '../../pipeline/reviewer/ReviewerGraph';
import CrossSceneBlock from './CrossSceneBlock';
import { readCrossScene } from './crossScene';
import ReviewFindingsList from './ReviewFindingsList';
import ReviewStepsList from './ReviewStepsList';
import ReviewTraceList from './ReviewTraceList';
import { buildReviewSteps, isUnreviewed } from './reviewSteps';
import VerdictCorrectionForm, { VerdictCorrectionBody } from './VerdictCorrectionForm';

/**
 * Tiers that never looked at an image: `keeps` is a TEXT check on a proposed
 * motion brief, `cast-bind` matches an uploaded picture to a story character
 * (recorded on the UPLOAD checkpoint), and `coherence` runs before any image
 * exists (recorded on a generator checkpoint). None of them may speak for a
 * checkpoint's frames.
 */
const NON_RULING_TIERS = new Set(['keeps', 'cast-bind', 'coherence']);

/**
 * The verdict a badge speaks for: verdicts are append-only, newest LAST — but
 * a non-ruling verdict is skipped, exactly as the backend's own
 * `lastRulingVerdict` skips it. None of `NON_RULING_TIERS` saw an image, so
 * one landing after a pair FAIL would badge the frames green (or grey) for a
 * call that never looked at them.
 *
 * An UNREVIEWED verdict is deliberately not skipped, although the backend's
 * skips it: the engine must not read an outage as a ruling, but the badge
 * exists to say that the frame went through without one.
 */
export const latestVerdict = (verdicts?: ReviewVerdict[]): ReviewVerdict | null => {
  for (let index = (verdicts || []).length - 1; index >= 0; index -= 1) {
    const verdict = (verdicts || [])[index];
    if (NON_RULING_TIERS.has((verdict.tier || '').trim().toLowerCase())) continue;
    return verdict;
  }
  return null;
};

/**
 * The newest verdict anywhere in a run, with the index of the checkpoint that
 * produced it — used by the distributor child cards, whose reviewed step is
 * inside the child run rather than on the card's own result.
 */
export const latestRunVerdict = (
  run: PipelineRun | null | undefined
): { verdict: ReviewVerdict; checkpointIndex: number } | null => {
  const results = run?.results || [];
  for (let index = results.length - 1; index >= 0; index -= 1) {
    const verdict = latestVerdict(results[index]?.verdicts);
    if (verdict) return { verdict, checkpointIndex: index };
  }
  return null;
};

export const failedFindings = (verdict: ReviewVerdict | null): ReviewFinding[] =>
  (verdict?.findings || []).filter((finding) => (finding.verdict || '').toUpperCase() === 'FAIL');

/** The shape of a verdict's `metadata.rewire`/`metadata.rewire_applied` object.
 * `drop_labels`/`add_labels` are only ever present on `rewire_applied` — the
 * human strings the engine renders from the resolved ids ("REFERENCE 2 (from
 * checkpoint:seed-upload, role seed_photo)", "checkpoint:anchor-scene") — the
 * raw `rewire` proposal never carries them. */
interface RewireInfo {
  drop?: string[];
  add?: string[];
  dropLabels?: string[];
  addLabels?: string[];
  reason?: string;
}

const asStringList = (value: unknown): string[] | undefined =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : undefined;

/** Reads a `metadata.rewire`/`rewire_applied` object off a verdict, tolerating
 * whatever a hand-rolled JSON payload throws at it. */
const asRewireInfo = (value: unknown): RewireInfo | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const drop = asStringList(record.drop);
  const add = asStringList(record.add);
  const dropLabels = asStringList(record.drop_labels);
  const addLabels = asStringList(record.add_labels);
  const reason = typeof record.reason === 'string' ? record.reason : undefined;
  if (!drop?.length && !add?.length && !reason) return null;
  return { drop, add, dropLabels, addLabels, reason };
};

/** "dropped x, y, added z — reason", omitting any part that's empty. Prefers
 * the human-readable `drop_labels`/`add_labels` over the raw resolved ids,
 * falling back to the ids when no labels came through. */
const formatRewireInfo = (info: RewireInfo): string => {
  const parts: string[] = [];
  const drop = info.dropLabels?.length ? info.dropLabels : info.drop;
  const add = info.addLabels?.length ? info.addLabels : info.add;
  if (drop?.length) parts.push(`dropped ${drop.join(', ')}`);
  if (add?.length) parts.push(`added ${add.join(', ')}`);
  const reason = (info.reason || '').trim();
  return `${parts.join(', ')}${reason ? ` — ${reason}` : ''}`;
};

/**
 * What to say about a verdict's attachment rewiring, if anything: what the
 * engine actually applied on the regeneration takes priority over the raw
 * judge proposal — a stale, unapplied `metadata.rewire` should never be read
 * as if it happened.
 */
const rewireRow = (
  verdict: ReviewVerdict
): { applied: boolean; text: string } | null => {
  const metadata = verdict.metadata || {};
  const applied = asRewireInfo(metadata.rewire_applied);
  if (applied) return { applied: true, text: `Reviewer rewired references: ${formatRewireInfo(applied)}` };

  const proposed = asRewireInfo(metadata.rewire);
  if (proposed) {
    return { applied: false, text: `Reviewer proposed (not applied): ${formatRewireInfo(proposed)}` };
  }
  return null;
};

/** How many advisory criteria a passing verdict noted. */
export const advisoryNoteCount = (verdict: ReviewVerdict): number => {
  const notes = (verdict.metadata || {}).advisory_failures;
  return Array.isArray(notes) ? notes.length : 0;
};

/**
 * An unreviewed verdict is checked FIRST: it carries `pass: false`, and read as
 * a ruling it would badge amber "review failed" for a frame nobody judged. A
 * verdict stored before outages were told apart has no `outcome` and keeps its
 * old amber badge — that record cannot say which of the two it was.
 */
const badgeLabel = (verdict: ReviewVerdict): string => {
  if (isUnreviewed(verdict)) return '? unreviewed';
  if (verdict.pass) {
    const notes = advisoryNoteCount(verdict);
    return notes ? `✓ reviewed · ${notes} note${notes === 1 ? '' : 's'}` : '✓ reviewed';
  }
  const failed = failedFindings(verdict).length;
  if (failed === 0) return '⚠ review failed';
  return failed === 1 ? '⚠ 1 criterion failed' : `⚠ ${failed} criteria failed`;
};

const badgeTitle = (verdict: ReviewVerdict): string => {
  if (isUnreviewed(verdict)) {
    const reason = (verdict.metadata || {}).review_unresolved || verdict.failure_kind;
    return `unreviewed — ${String(reason || 'no verdict')}`;
  }
  const parts = [`${verdict.tier || 'check'} tier`, `attempt ${verdict.attempt || 1}`];
  if (Number.isFinite(verdict.confidence) && verdict.confidence > 0) {
    parts.push(`confidence ${verdict.confidence}`);
  }
  if (verdict.severity) parts.push(`severity ${verdict.severity}`);
  if (verdict.model) parts.push(verdict.model);
  return parts.join(' · ');
};

/** What a verdict's `metadata.correction` says, once a human overrode it. */
interface CorrectionInfo {
  label: ReviewCorrection['label'] | string;
  reason: string;
}

const asCorrectionInfo = (value: unknown): CorrectionInfo | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const label = typeof record.label === 'string' ? record.label : '';
  if (!label) return null;
  return { label, reason: typeof record.reason === 'string' ? record.reason : '' };
};

/**
 * The reviewer calls that produced one verdict.
 *
 * The verdict id is the exact link — `callJudge` stamps the same id on the
 * verdict and on its trace — and it is tried first. But it is not the ONLY
 * link, and treating it as one is what made a pair-tier verdict read "No trace
 * recorded" with its trace sitting right there: an id can go missing on the way
 * (a judge call that errored is traced with an EMPTY verdict id, a trace evicted
 * from the run's per-checkpoint cap and re-served from the store, a row written
 * before the id existed), and the coordinate match was unreachable for every
 * verdict that HAD an id.
 *
 * So the coordinates are a genuine fallback now: (attempt, tier, frame), which
 * `callJudge` writes identically onto both records for every tier — including
 * `pair` (frame "pair") and `keeps` (frame "video-prompt"). An absent frame on
 * either side means the opening frame; the wire omits it (`omitempty`).
 */
const sameReviewCall = (trace: ReviewTrace, verdict: ReviewVerdict): boolean =>
  trace.attempt === verdict.attempt &&
  (trace.tier || '') === (verdict.tier || '') &&
  (trace.frame || '') === (verdict.frame || '');

/**
 * The reply a retried judge call did NOT keep, by trace id. No verdict came
 * from it, so it carries no verdict id: the id link never finds it, and the
 * coordinate fallback only runs when the id link found nothing. The verdict's
 * `metadata.judge_retry.first_trace_id` names it instead.
 */
const retriedTraceId = (verdict: ReviewVerdict): string => {
  const retry = (verdict.metadata || {}).judge_retry;
  const id = retry && typeof retry === 'object' ? (retry as Record<string, unknown>).first_trace_id : undefined;
  return typeof id === 'string' ? id : '';
};

/** Every call behind the verdict; a retried-away reply reads first, above the one that stood. */
export const tracesForVerdict = (traces: ReviewTrace[], verdict: ReviewVerdict): ReviewTrace[] => {
  const firstId = retriedTraceId(verdict);
  const withRetried = (list: ReviewTrace[]) => {
    const retried = firstId ? traces.filter((trace) => trace.id === firstId && !list.includes(trace)) : [];
    return [...retried, ...list];
  };
  if (verdict.id) {
    const linked = traces.filter((trace) => trace.verdict_id === verdict.id);
    if (linked.length > 0) return withRetried(linked);
  }
  return withRetried(traces.filter((trace) => sameReviewCall(trace, verdict)));
};

interface CheckpointVerdictPanelProps {
  verdicts?: ReviewVerdict[];
  /**
   * The ROOT run's cast. A failed check about one of these characters shows
   * their reference picture beside the finding. Omit it — as every surface
   * outside a scene does — and the panel is exactly what it was.
   */
  cast?: CastMember[];
  /** Called with the verdict's adjusted prompt; omit to hide the action. */
  onRegenerateWithAdjustedPrompt?: (adjustedPrompt: string) => void;
  /**
   * Re-runs the EARLIER scene the verdict says this frame contradicts, with the
   * reviewer's corrective note. Provided only by a surface that can address
   * another scene — the sequencer rail can, a distributor's scene card cannot —
   * and without it the suggestion is shown without a button to act on it.
   *
   * It resolves the scene id to a step itself, and rejecting is how it reports
   * both a scene it cannot place and a backend that refused.
   */
  onFixEarlierScene?: (target: { sceneId: string; what: string }) => Promise<void>;
  busy?: boolean;
  /**
   * What went wrong with the panel's OWN regenerate, shown under its button.
   * A 409 here is the backend refusing a mid-run regeneration ("timed out
   * waiting for the run to pause…"), which the operator has to read to know
   * the step was left alone rather than broken.
   */
  error?: string;
  /**
   * Shown while that regenerate is still in flight. The request stays open
   * until the engine parks the run — minutes, potentially — so the panel says
   * what it is waiting for instead of just disabling the button.
   */
  waiting?: string;
  /** Distinguishes the badge/panel ids when several render in one view. */
  idPrefix?: string;
  /**
   * The run that OWNS these verdicts — the child run for a sequencer step.
   * Omit it and the panel is exactly what it was before traces existed: no
   * reviewer trace, no correction action.
   */
  runId?: string;
  /**
   * The template and checkpoint these verdicts were produced under. Both
   * together are what the reviewer PICTURE is addressed by — omit either and
   * the panel is exactly what it was, with no "Show the reviewer" action.
   */
  templateId?: string;
  checkpointId?: string;
  /**
   * `<provider>/<model>` of that checkpoint's generator, when it declares one.
   * A correction's lesson can then be scoped to the MODEL — a rule about how
   * one generator behaves, shown to every judge whose checkpoint uses it.
   */
  modelId?: string;
  /** The verdict as the backend restated it after a correction was recorded. */
  onCorrected?: (verdict: ReviewVerdict) => void;
  /**
   * Drops (hard-off) the closing frame of the scene this verdict belongs to,
   * with the reason to record it under. Provided ONLY by a surface that owns a
   * droppable closing frame — the correction dialog offers the reviewer's
   * suggested repair exactly when it can actually be carried out, and never
   * ticks a box that does nothing.
   */
  onDropClosingFrame?: (reason: string) => Promise<void>;
}

/**
 * The AI reviewer's read on a checkpoint's output: a badge derived from the
 * newest verdict, expanding into the findings that produced it. Renders
 * nothing at all when the checkpoint was never reviewed, so templates without
 * a reviewer look exactly as they did.
 */
const CheckpointVerdictPanel: React.FC<CheckpointVerdictPanelProps> = ({
  verdicts,
  cast,
  onRegenerateWithAdjustedPrompt,
  busy,
  error,
  waiting,
  idPrefix = 'verdict',
  runId,
  templateId,
  checkpointId,
  modelId,
  onCorrected,
  onDropClosingFrame,
  onFixEarlierScene,
}) => {
  const [open, setOpen] = React.useState(false);
  /**
   * Whether the per-call breakdown is expanded. Lifted out of the list because
   * expanding it is what pays for the checkpoint's gates — the panel must not
   * read the reviewer profile for a badge nobody opened.
   */
  const [stepsOpen, setStepsOpen] = React.useState(false);
  const [reviewerOpen, setReviewerOpen] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [correctionError, setCorrectionError] = React.useState('');
  /**
   * Why the closing-frame drop failed. Kept apart from `correctionError`
   * because the two outcomes are independent: the correction is recorded first
   * and STAYS recorded, whatever the repair that followed it did.
   */
  const [dropError, setDropError] = React.useState('');
  /**
   * The correction this session just recorded, before the refetched run
   * carries it — tied to the verdict it was recorded ON, so a regeneration
   * that appends a NEW verdict is not painted as already corrected.
   */
  const [savedCorrection, setSavedCorrection] = React.useState<{
    verdictId: string;
    info: CorrectionInfo;
  } | null>(null);
  const { traces, loading: tracesLoading, error: tracesError, source, refetch } = useReviewTraces(
    runId,
    open && Boolean(runId)
  );
  // The gates the derived "what happened next" lines quote. Fetched only once
  // the breakdown is actually open; without them a stopped check is reported
  // as "not escalated" and no number is invented.
  const { profile } = useReviewProfile(
    stepsOpen && templateId ? templateId : undefined
  );
  // Escape closes the reviewer overlay, like every other full-screen surface.
  React.useEffect(() => {
    if (!reviewerOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setReviewerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [reviewerOpen]);

  const canShowReviewer = Boolean(templateId && checkpointId);
  const verdict = latestVerdict(verdicts);
  if (!verdict) return null;

  const unreviewed = isUnreviewed(verdict);
  const findings = verdict.findings || [];
  const adjustedPrompt = (verdict.adjusted_prompt || '').trim();
  const rewire = rewireRow(verdict);
  const crossScene = readCrossScene(verdict);
  const panelId = `${idPrefix}-panel`;
  const correction =
    (savedCorrection && savedCorrection.verdictId === verdict.id ? savedCorrection.info : null) ||
    asCorrectionInfo((verdict.metadata || {}).correction);
  const gates =
    profile?.checkpoints.find((candidate) => candidate.checkpoint_id === checkpointId)?.gates ||
    null;
  const steps = buildReviewSteps(verdicts, verdict, traces, gates);

  const handleCorrect = async (body: VerdictCorrectionBody) => {
    if (!runId || !verdict.id) return;
    setSaving(true);
    setCorrectionError('');
    setDropError('');
    const { dropClosingFrame, ...payload } = body;
    try {
      const response = await PipelineAPI.correctVerdict(runId, verdict.id, payload);
      const updated = response?.verdict;
      setSavedCorrection({
        verdictId: verdict.id,
        info: asCorrectionInfo(updated?.metadata?.correction) || {
          label: body.label,
          reason: body.reason,
        },
      });
      setFormOpen(false);
      // The trace log is what a correction is judged against — re-read it so
      // the panel is not showing a pre-correction record.
      refetch();
      if (updated) onCorrected?.(updated);
      // The repair the operator ticked runs only once the correction is safely
      // recorded, and its failure never un-records it — hence its own try.
      if (dropClosingFrame && onDropClosingFrame) {
        try {
          await onDropClosingFrame(payload.lesson || payload.reason);
        } catch (dropErr) {
          setDropError(
            dropErr instanceof Error && dropErr.message
              ? `The correction was saved, but the closing frame was not dropped: ${dropErr.message}`
              : 'The correction was saved, but the closing frame was not dropped.'
          );
        }
      }
    } catch (err) {
      setCorrectionError(
        err instanceof Error && err.message ? err.message : 'Could not record the correction.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        data-testid="review-verdict-badge"
        data-state={unreviewed ? 'unreviewed' : verdict.pass ? 'pass' : 'fail'}
        aria-expanded={open}
        aria-controls={panelId}
        title={badgeTitle(verdict)}
        onClick={() => {
          const next = !open;
          setOpen(next);
          // A half-typed correction does not survive the panel closing, so the
          // form must not be waiting, empty, when it is reopened.
          if (!next) {
            setFormOpen(false);
            setCorrectionError('');
            setDropError('');
            setStepsOpen(false);
          }
        }}
        className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-wide transition-colors ${
          unreviewed
            ? 'border-dashed border-white/20 text-zinc-500 hover:text-zinc-300'
            : verdict.pass
              ? 'border-white/15 text-zinc-400 hover:text-zinc-200'
              : 'border-amber-400/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20'
        }`}
      >
        {badgeLabel(verdict)}
      </button>

      {open && (
        <div
          id={panelId}
          data-testid="review-verdict-panel"
          className="space-y-2 border border-white/10 bg-black/50 p-2"
        >
          <ReviewFindingsList findings={findings} cast={cast} />

          {verdict.critique && (
            <p className="text-[11px] leading-relaxed text-zinc-300 whitespace-pre-wrap">
              {verdict.critique}
            </p>
          )}

          {rewire && (
            <p
              data-testid="review-verdict-rewire"
              data-variant={rewire.applied ? 'applied' : 'proposed'}
              className={`text-[11px] leading-relaxed whitespace-pre-wrap ${
                rewire.applied ? 'text-zinc-300' : 'italic text-zinc-500'
              }`}
            >
              {rewire.text}
            </p>
          )}

          {crossScene && (
            <CrossSceneBlock
              // A NEW proposal is a new question, so it gets a new block: keyed
              // by what it asks for, one already requested by hand cannot leave
              // its "Requested" line standing over a different suggestion.
              key={`${crossScene.sceneId}:${crossScene.what}`}
              suggestion={crossScene}
              onFix={onFixEarlierScene}
              disabled={busy}
            />
          )}

          {adjustedPrompt && (
            <details>
              <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-zinc-500">
                Adjusted prompt
              </summary>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap border border-white/10 bg-black/70 p-2 font-mono text-[11px] text-slate-300">
                {adjustedPrompt}
              </pre>
            </details>
          )}

          {adjustedPrompt && onRegenerateWithAdjustedPrompt && (
            <div className="space-y-1">
              <button
                type="button"
                className="btn btn-sm btn-secondary text-[10px]"
                disabled={busy}
                onClick={() => onRegenerateWithAdjustedPrompt(adjustedPrompt)}
              >
                {busy ? 'Regenerating…' : 'Regenerate with adjusted prompt'}
              </button>
              {waiting && (
                <p className="text-[10px] text-sky-300" data-testid="review-verdict-regenerate-waiting">
                  {waiting}
                </p>
              )}
              {error && (
                <p className="text-[10px] text-red-300" data-testid="review-verdict-regenerate-error">
                  {error}
                </p>
              )}
            </div>
          )}

          {/* The whole cascade, collapsed. It sits between the newest
              verdict's findings and "Inside the reviewer" because that is the
              order the questions come in: what did it say, how did it get
              there, what did it actually read. */}
          <ReviewStepsList
            steps={steps}
            open={stepsOpen}
            onToggle={() => setStepsOpen((current) => !current)}
            gatesKnown={Boolean(gates)}
          />

          {runId && (
            <>
              <ReviewTraceList
                traces={tracesForVerdict(traces, verdict)}
                loading={tracesLoading}
                error={tracesError}
                source={source}
                action={
                  canShowReviewer ? (
                    <button
                      type="button"
                      data-testid="review-show-reviewer"
                      className="btn btn-sm btn-ghost text-[10px]"
                      title="The whole reviewer for this checkpoint, with what this run did"
                      onClick={() => setReviewerOpen(true)}
                    >
                      Show the reviewer
                    </button>
                  ) : undefined
                }
              />

              {reviewerOpen && templateId && checkpointId && (
                <div
                  className="fixed inset-0 z-[220] flex items-center justify-center bg-black/85 p-4"
                  data-testid="reviewer-graph-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-label="The reviewer"
                >
                  <div className="flex h-full w-full flex-col border border-white/20 bg-zinc-950 shadow-xl">
                    <div className="flex flex-shrink-0 items-center gap-3 border-b border-white/10 bg-black/60 px-4 py-3">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-white">
                        The reviewer
                      </h3>
                      <span className="truncate font-mono text-[11px] text-white/40">
                        {checkpointId}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost ml-auto"
                        data-testid="reviewer-graph-close"
                        onClick={() => setReviewerOpen(false)}
                      >
                        Close
                      </button>
                    </div>
                    <div className="flex-1 overflow-auto p-3">
                      {/* Read-only here: this is the run's record, not a place
                          to change what the reviewer may do. And it is THIS
                          verdict's record: the evidence is scoped exactly as
                          the trace list above it is, so a closing-frame panel
                          can never badge the opening frame's judge calls. */}
                      <ReviewerGraph
                        templateId={templateId}
                        checkpointId={checkpointId}
                        traces={tracesForVerdict(traces, verdict)}
                        verdicts={[verdict]}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* An unreviewed verdict is no ruling, so there is nothing to call
                  wrong: the frame was kept without one. */}
              {!unreviewed && (
                <div className="space-y-1 border-t border-white/10 pt-2">
                  {correction ? (
                    <span
                      data-testid="review-correction-badge"
                      title={correction.reason}
                      className="inline-flex items-center gap-1 border border-amber-400/50 bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-200"
                    >
                      corrected · {correction.label.replace('_', ' ')}
                    </span>
                  ) : (
                    <>
                      {formOpen ? (
                        <VerdictCorrectionForm
                          verdict={verdict}
                          busy={saving}
                          runId={runId}
                          templateId={templateId}
                          checkpointId={checkpointId}
                          modelId={modelId}
                          canDropClosingFrame={Boolean(onDropClosingFrame)}
                          onSubmit={handleCorrect}
                          onCancel={() => {
                            setFormOpen(false);
                            setCorrectionError('');
                            setDropError('');
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary text-[10px]"
                          disabled={!verdict.id}
                          title={verdict.id ? undefined : 'This verdict predates corrections'}
                          onClick={() => setFormOpen(true)}
                        >
                          Wrong verdict
                        </button>
                      )}
                      {correctionError && (
                        <p className="text-[10px] text-red-300" data-testid="review-correction-error">
                          {correctionError}
                        </p>
                      )}
                    </>
                  )}
                  {dropError && (
                    <p
                      className="text-[10px] text-amber-300"
                      data-testid="review-correction-action-error"
                    >
                      {dropError}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckpointVerdictPanel;
